// The route table — replaces Next's file-based routing.
//
// The shape mirrors the old app/ tree: one public route (/login) and everything
// else behind the guard, inside the shared private layout. That is the same
// arrangement `proxy.ts` enforced, only now it's visible in one file rather
// than implied by folder names.

import { lazy } from "react";
import { createBrowserRouter, Navigate } from "react-router";
import { SessionProvider, ProtectedRoute } from "./api/session";
import { PrivateLayout } from "./components/PrivateLayout";
import { ErroDaRota } from "./components/ui/ErroDaRota";
import { Login } from "./pages/Login";
import { Inicio } from "./pages/Inicio";
import { Timeline } from "./pages/Timeline";
import { Conto } from "./pages/Conto";
import { ParaSempre } from "./pages/ParaSempre";
import { Jogos } from "./pages/Jogos";
import { JogosEmoji } from "./pages/JogosEmoji";
import { JogosHistorico } from "./pages/JogosHistorico";
import { Notificacoes } from "./pages/Notificacoes";
import { Definicoes } from "./pages/Definicoes";
/**
 * Code-split, because these four are the expensive ones and none of them is on
 * the first screen (IDEA-014/030/031/032).
 *
 * The map alone pulls in Leaflet, and the two boards pull in the puzzle solver
 * and generator. Loading them eagerly made the entry bundle 756 kB — everyone
 * paying, on mobile data, for pages they open occasionally. Split out, opening
 * the app costs a third less and the map fetches its own chunk when asked for.
 * PrivateLayout carries the <Suspense> that covers the wait.
 */
const Mapa = lazy(() => import("./pages/Mapa").then((m) => ({ default: m.Mapa })));
const Calendario = lazy(() =>
  import("./pages/Calendario").then((m) => ({ default: m.Calendario })),
);
const JogosMemoria = lazy(() =>
  import("./pages/JogosMemoria").then((m) => ({ default: m.JogosMemoria })),
);
const JogosShikaku = lazy(() =>
  import("./pages/JogosShikaku").then((m) => ({ default: m.JogosShikaku })),
);
const JogosSudoku = lazy(() =>
  import("./pages/JogosSudoku").then((m) => ({ default: m.JogosSudoku })),
);
const JogosPinpoint = lazy(() =>
  import("./pages/JogosPinpoint").then((m) => ({ default: m.JogosPinpoint })),
);
const JogosWend = lazy(() =>
  import("./pages/JogosWend").then((m) => ({ default: m.JogosWend })),
);
const JogosTango = lazy(() =>
  import("./pages/JogosTango").then((m) => ({ default: m.JogosTango })),
);
const JogosQueens = lazy(() =>
  import("./pages/JogosQueens").then((m) => ({ default: m.JogosQueens })),
);
const JogosZip = lazy(() =>
  import("./pages/JogosZip").then((m) => ({ default: m.JogosZip })),
);
const JogosCrossclimb = lazy(() =>
  import("./pages/JogosCrossclimb").then((m) => ({ default: m.JogosCrossclimb })),
);
import { Album } from "./pages/Album";
import { AlbumPastas } from "./pages/AlbumPastas";
import { ContoAdicionar } from "./pages/ContoAdicionar";
import { ParaSempreAdicionar } from "./pages/ParaSempreAdicionar";

/**
 * Wraps everything so the session resolves once, above the router's outlet.
 * SessionProvider needs to sit inside the router (it reads the location) but
 * outside the guard (the guard reads the session).
 */
function Root() {
  return (
    <SessionProvider>
      <ProtectedRoute />
    </SessionProvider>
  );
}

export const router = createBrowserRouter([
  {
    path: "/login",
    errorElement: <ErroDaRota />,
    element: (
      <SessionProvider>
        <Login />
      </SessionProvider>
    ),
  },
  {
    // Apanha o que rebentar em qualquer página de dentro — incluindo as que são
    // carregadas à parte e cujo ficheiro pode já não existir depois de um
    // deploy. Sem isto o que aparecia era o ecrã em bruto do React Router.
    errorElement: <ErroDaRota />,
    element: <Root />,
    children: [
      {
        element: <PrivateLayout />,
        children: [
          { path: "/", element: <Inicio /> },
          { path: "/timeline", element: <Timeline /> },
          { path: "/conto", element: <Conto /> },
          { path: "/para-sempre", element: <ParaSempre /> },
          { path: "/jogos", element: <Jogos /> },
          // IDEA-044 — os avisos estão na barra de baixo, as definições no menu
          // da conta lá em cima.
          { path: "/notificacoes", element: <Notificacoes /> },
          { path: "/definicoes", element: <Definicoes /> },
          { path: "/mapa", element: <Mapa /> },
          { path: "/calendario", element: <Calendario /> },

          { path: "/album", element: <Album /> },
          { path: "/album/pastas", element: <AlbumPastas /> },
          { path: "/conto/adicionar", element: <ContoAdicionar /> },
          { path: "/para-sempre/adicionar", element: <ParaSempreAdicionar /> },
          { path: "/jogos/emoji", element: <JogosEmoji /> },
          { path: "/jogos/emoji/historico", element: <JogosHistorico /> },
          { path: "/jogos/memoria", element: <JogosMemoria /> },
          { path: "/jogos/shikaku", element: <JogosShikaku /> },
          { path: "/jogos/sudoku", element: <JogosSudoku /> },
          { path: "/jogos/pinpoint", element: <JogosPinpoint /> },
          { path: "/jogos/wend", element: <JogosWend /> },
          { path: "/jogos/tango", element: <JogosTango /> },
          { path: "/jogos/queens", element: <JogosQueens /> },
          { path: "/jogos/crossclimb", element: <JogosCrossclimb /> },
          { path: "/jogos/zip", element: <JogosZip /> },

          // Anything else inside the private area goes home rather than showing
          // a dead end.
          { path: "*", element: <Navigate to="/" replace /> },
        ],
      },
    ],
  },
]);
